import "../global.css";

import { configSite, configVWO } from "@pub/config";
import { AffiliateMarketingProvider } from "@sphtech/affiliate-marketing-library";
import {
  BaseProvider,
  createHideMobileBannerSEF,
} from "@sphtech/dmg-design-system";
import { ResponseType } from "@sphtech/web2-core/ssr";
import NeuronProvider from "@web2/providers/NeuronProvider";
import NewRelic from "@web2/providers/NewRelic";
import { StrictMode } from "react";
import {
  isRouteErrorResponse,
  Links,
  LinksFunction,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";
import { SWRConfig } from "swr";

import { useRouteData } from "./components/hooks/useRouteData";
import { getPubShorthand } from "./components/Modules/AffiliatesMarketingWidget/utils";
import { NEURON_CONFIG_NAME, VWO_SMARTCODE_URL } from "./constants";
import ErrorLayout from "./layouts/ErrorLayout/ErrorLayout";
import AnalyticsSettings from "./utils/AnalyticsSettings";
import { getVwoSmartCode } from "./utils/getVwoSmartCode";
import { fetcher } from "./utils/swr";
import { Route } from ".react-router/types/src/app/+types/root";

export const links: LinksFunction = () => {
  return configSite.links ?? [];
};

// Wrapper component that sets up the HTML shell and global scripts
export function Layout({ children }: { children: React.ReactNode }) {
  const { globalData, includeScripts } = useRouteData();

  const mobileBannerConfig = globalData?.magazineIssues?.config?.mobileBanner;

  return (
    <html
      lang={configSite.lang ?? "en-SG"}
      className={`theme-${import.meta.env.VITE_PUBLICATION}`}
      suppressHydrationWarning
    >
      <head>
        {/* --- GLOBAL META AND LINKS --- */}
        <meta charSet="UTF-8" />
        <meta content="width" name="MobileOptimized" />
        <meta content="true" name="HandheldFriendly" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <meta content="ie=edge" httpEquiv="x-ua-compatible" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link rel="manifest" href="/site.webmanifest" />

        {/* --- DYNAMIC LINKS (Favicons/CSS) --- */}
        <Links />

        {/* --- DYNAMIC META (Page Titles/Description) --- */}
        <Meta />

        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: `
              window.SPHMGAM = window.SPHMGAM || { cmd: [] };
              window.SPHMGTM = window.SPHMGTM || { cmd: [] };
              ${createHideMobileBannerSEF(mobileBannerConfig?.cookieKey)}
            `,
          }}
        />

        {configVWO.active && (
          <>
            <link rel="preconnect" href={VWO_SMARTCODE_URL} />
            <script
              type="text/javascript"
              id="vwoCode"
              dangerouslySetInnerHTML={{
                __html: getVwoSmartCode(configVWO.accountId, VWO_SMARTCODE_URL),
              }}
            />
          </>
        )}
      </head>

      <body>
        {/* --- Innity Flexframe XXL ad unit: https://sph.atlassian.net/browse/DMG-6060 --- */}
        <div id="innity-flexframe-xxl-placeholder"></div>
        <div id="sphm_overlay"></div>
        <div id="app">{children}</div>

        <ScrollRestoration />
        {includeScripts ? <Scripts /> : null}
      </body>
    </html>
  );
}

// Main application component
export default function App() {
  const { globalData } = useRouteData();

  return (
    <StrictMode>
      <NeuronProvider configName={NEURON_CONFIG_NAME}>
        <SWRConfig value={{ fetcher }}>
          <NewRelic />
          <BaseProvider data={globalData}>
            <AffiliateMarketingProvider
              config={{
                env: import.meta.env.VITE_AM_ENV || "prod",
                pub: getPubShorthand(import.meta.env.VITE_PUBLICATION),
              }}
            >
              <Outlet />
            </AffiliateMarketingProvider>
            <AnalyticsSettings />
          </BaseProvider>
        </SWRConfig>
      </NeuronProvider>
    </StrictMode>
  );
}

// Runs in the browser and catches errors that occur during rendering of React components on the client.
// I.e Catches that occur during CLIENT side routing
// Errors during rendering, lifecycle methods, and constructors in the React component tree.
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  if (isRouteErrorResponse(error)) {
    return (
      <ErrorLayout
        response={{
          statusCode: 500,
          type: ResponseType.SERVER_ERROR,
          payload: error.statusText,
        }}
        clientSideError={true}
      />
    );
  } else if (error instanceof Error && import.meta.env.DEV) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error.message}</p>
        <p>The stack trace is:</p>
        <pre>{error.stack}</pre>
      </div>
    );
  } else {
    return (
      <ErrorLayout
        response={{
          statusCode: 500,
          type: ResponseType.SERVER_ERROR,
          payload: { message: "Unknown error" },
        }}
        clientSideError={true}
      />
    );
  }
}

export function loader({ request }: Route.LoaderArgs) {
  // Skip data fetching for error pages during prerendering
  const url = new URL(request.url);
  if (url.pathname.startsWith("/error/")) {
    return null;
  }
}

// Prevents re-fetching the root route loader data during client-side navigation (/search page)
export function shouldRevalidate() {
  return false;
}
